// Step 1: what kind of work the group does. Choosing another type clears the workflow, which
// belongs to a type, or chooses it when the type has only one.
import { Tag } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { CatalogIcon } from "../../../catalog/icons";
import { useT } from "../../../i18n";
import { EmptyState, ErrorState, Loading } from "../../../ui/states";
import { marketplaceTypesQuery } from "../../lookups";
import { marketplaceWorkflowsQuery } from "../api";
import type { Draft } from "../draft";
import { ChoiceCard } from "../frame/ChoiceCard";

export function TypeStep({ draft, onChange }: { draft: Draft; onChange: (patch: Partial<Draft>) => void }) {
  const t = useT().tasks.create.type;
  const types = useQuery(marketplaceTypesQuery());
  const queryClient = useQueryClient();
  const choose = async (taskTypeId: string) => {
    if (draft.taskTypeId === taskTypeId) return;
    onChange({ taskTypeId, workflow: null, bindings: {} });
    const first = await queryClient.fetchQuery(marketplaceWorkflowsQuery(taskTypeId)).catch(() => null);
    if (first?.items.length === 1 && !first.hasNext) onChange({ workflow: first.items[0]! });
  };
  if (types.isPending) return <Loading />;
  if (types.isError) return <ErrorState title={t.loadFailed} message={types.error.message} />;
  const list = types.data.filter((type) => type.enabled);
  if (list.length === 0) {
    return (
      <EmptyState compact icon={<Tag weight="duotone" />} title={t.none}>
        {t.noneBody}
      </EmptyState>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2" role="radiogroup" aria-label={t.label}>
      {list.map((type) => (
        <ChoiceCard
          key={type.id}
          name="task-type"
          value={type.id}
          checked={draft.taskTypeId === type.id}
          onSelect={() => void choose(type.id)}
          icon={<CatalogIcon name={type.icon} />}
          title={type.name}
        >
          {type.description}
        </ChoiceCard>
      ))}
    </div>
  );
}
